"use client";

import { useMemo, useState } from "react";

import { convertToAssetId, HydratedChatbot, Member } from "@zapier/client";
import { ChatbotDeleteModal } from "../ChatbotDeleteModal/ChatbotDeleteModal";
import ChatbotChangeOwnerModal from "../ChatbotChangeOwnerModal/ChatbotChangeOwnerModal";
import { ChatbotsShareModal } from "../ChatbotsShareModal/ChatbotsShareModal";
import { ChatbotRenameModal } from "../ChatbotRenameModal";
import { ChatbotUpgradeModal } from "../ChatbotUpgradeModal";
import { AssetTypes } from "@zapier/utils";
import { MoveAssetModal } from "@zapier/shared";

export enum ChatbotModal {
  Delete = "chatbot__delete",
  ChangeOwner = "chatbot__changeOwner",
  Share = "chatbot__share",
  Move = "chatbot__move",
  Rename = "chatbot__rename",
  Upgrade = "chatbot__upgrade",
}

interface Props {
  currentModal: ChatbotModal | undefined;
  currentChatbot: HydratedChatbot | undefined;
  setCurrentChatbot: (chatbot?: HydratedChatbot | undefined) => void;
  setCurrentModal: (modal?: ChatbotModal | undefined) => void;
  folderId?: string; // used for cache invalidation in mutations
}

export function ChatbotModals({
  currentModal,
  currentChatbot,
  setCurrentChatbot,
  setCurrentModal,
  folderId,
}: Props) {
  const handleCloseModal = () => {
    setCurrentModal();
    setCurrentChatbot(undefined);
    setNewOwner(undefined);
  };

  const [newOwner, setNewOwner] = useState<Member | undefined>();

  const assetIds = useMemo(() => {
    if (!currentChatbot?.id) {
      return;
    }
    return [convertToAssetId(AssetTypes.Chatbot, currentChatbot.id)];
  }, [currentChatbot?.id]);

  return (
    <>
      <ChatbotRenameModal
        isOpen={currentModal === ChatbotModal.Rename}
        onClose={handleCloseModal}
        chatbot={currentChatbot}
        folderId={folderId}
      />

      <ChatbotChangeOwnerModal
        isOpen={currentModal === ChatbotModal.ChangeOwner}
        onCloseModal={handleCloseModal}
        chatbot={currentChatbot}
        preselectedUser={newOwner}
        folderId={folderId}
      />

      <ChatbotDeleteModal
        isOpen={currentModal === ChatbotModal.Delete}
        onClose={handleCloseModal}
        chatbot={currentChatbot}
        folderId={folderId}
      />

      <ChatbotsShareModal
        chatbot={currentChatbot}
        isOpen={currentModal === ChatbotModal.Share}
        onCloseModal={handleCloseModal}
      />

      <MoveAssetModal
        assetType={AssetTypes.Chatbot}
        assetIds={assetIds}
        assetTitle={currentChatbot?.name}
        isOpen={currentModal === ChatbotModal.Move}
        setIsOpen={handleCloseModal}
        assetParentId={folderId}
      />

      <ChatbotUpgradeModal
        isOpen={currentModal === ChatbotModal.Upgrade}
        onClose={handleCloseModal}
        chatbotName={currentChatbot?.name ?? undefined}
      />
    </>
  );
}
